# Testing

In our [Playground environment],
the [`advertisementProducts`] query defaults to a response that indicates the hirer will pay via an existing contract.
You can override this behaviour and trigger specific testing scenarios with the hardcoded position titles listed below.

These Playground responses may use static product [`id`]s for deterministic testing,
but you should not carry this assumption across to production.
Due to the variable nature of SEEK’s product ladder,
the new panel and GraphQL query return ephemeral identifiers that reference the set of products quoted at that point in time.

Products presented to a hirer are only valid within the context of the workflow that they were executing at the time.
The selected product identifier may be temporarily stored in a job ad draft, for example,
but it must not be persisted long term alongside a posted job ad or to build a hardcoded mapping.

[`advertisementproducts`]: /schema/query/advertisementProducts
[`id`]: /schema/named-type/AdvertisementProduct#/field/id
[playground environment]: /graphql/playground

## `[FORCE FOUR]`

This position title will trigger a response that includes four ad products.
The response is for demonstration purposes only and may not be representative of actual ad products that hirers can expect to see in production.

```graphql
query advertisementProducts(
  $positionProfile: AdvertisementProducts_PositionProfileInput!
) {
  advertisementProducts(positionProfile: $positionProfile) {
    products {
      id {
        value
      }
      label
    }
  }
}
```

```json
{
  "positionProfile": {
    "jobCategories": "seekAnzPublicTest:jobCategory:seek:72J8gpm5",
    "positionLocation": "seekAnzPublicTest:location:seek:2FqwWaaMV",
    "positionOrganizations": "seekAnzPublicTest:organization:seek:93WyyF1h",
    "positionTitle": "[FORCE FOUR]"
  }
}
```

```json
{
  "advertisementProducts": {
    "products": [
      {
        "id": {
          "value": "globalPublicTest:adProduct:seekApi:EWXUBkp6A1CoJGCv4SLT3y3cKPi2fjZSCWUezjAMbVnr"
        },
        "label": "Classic"
      },
      {
        "id": {
          "value": "globalPublicTest:adProduct:seekApi:EWXUBkp6A1CoJGCv4SLT3y3bse71gUgyEUt8U93n47hr"
        },
        "label": "Branded"
      },
      {
        "id": {
          "value": "globalPublicTest:adProduct:seekApi:EWXUBkp6A1CoJGCv4SLT3y2tdSSKtCwJLRoVtxLiiP8x"
        },
        "label": "Premium"
      },
      {
        "id": {
          "value": "globalPublicTest:adProduct:seekApi:EWXUBkp6A1CoJGCv4SLT3y9Jb1ScpCukeN5pU11ZvYXk"
        },
        "label": "Premium Plus"
      }
    ]
  }
}
```

## `[NO CONTRACT]`

This position title will trigger a response that indicates the hirer will be invoiced.

```graphql
query advertisementProducts(
  $positionProfile: AdvertisementProducts_PositionProfileInput!
) {
  advertisementProducts(positionProfile: $positionProfile) {
    products {
      id {
        value
      }
      label
      payment {
        summaryHtml
      }
    }
  }
}
```

```json
{
  "positionProfile": {
    "jobCategories": "seekAnzPublicTest:jobCategory:seek:72J8gpm5",
    "positionLocation": "seekAnzPublicTest:location:seek:2FqwWaaMV",
    "positionOrganizations": "seekAnzPublicTest:organization:seek:93WyyF1h",
    "positionTitle": "[NO CONTRACT]"
  }
}
```

```json
{
  "advertisementProducts": {
    "products": [
      {
        "id": {
          "value": "globalPublicTest:adProduct:seekApi:EWHKTY9D6mJFiA5EH6P7Jr4Xs5YjNg7h39D39n5c7WVx"
        },
        "label": "Classic",
        "payment": {
          "summaryHtml": "You’ll be invoiced <strong>AUD 269.89 + GST</strong>."
        }
      },
      {
        "id": {
          "value": "globalPublicTest:adProduct:seekApi:EWHKTY9D6mJFiA5EH6P7Jr4XRKwiPRFE57cWdBy2a8Qx"
        },
        "label": "Branded",
        "payment": {
          "summaryHtml": "You’ll be invoiced <strong>AUD 414.09 + GST</strong>."
        }
      },
      {
        "id": {
          "value": "globalPublicTest:adProduct:seekApi:EWHKTY9D6mJFiA5EH6P7Jr3pB8H2b9VZB4Xt41FyEPr4"
        },
        "label": "Premium",
        "payment": {
          "summaryHtml": "You’ll be invoiced <strong>AUD 645.00 + GST</strong>."
        }
      }
    ]
  }
}
```

## `[EDIT PRICE]`

This position title will trigger a response that contains an edit price.
The response is for demonstration purposes only and may not be representative of actual ad products or system behaviour that hirers can expect to see in production.

```graphql
query advertisementProducts(
  $positionProfile: AdvertisementProducts_PositionProfileInput!
) {
  advertisementProducts(positionProfile: $positionProfile) {
    products {
      id {
        value
      }
      label
      price {
        summary
      }
      payment {
        summaryHtml
      }
    }
  }
}
```

```json
{
  "positionProfile": {
    "jobCategories": "seekAnzPublicTest:jobCategory:seek:72J8gpm5",
    "positionLocation": "seekAnzPublicTest:location:seek:2FqwWaaMV",
    "positionOrganizations": "seekAnzPublicTest:organization:seek:93WyyF1h",
    "positionTitle": "[EDIT PRICE]",
    "profileId": "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV"
  }
}
```

```json
{
  "advertisementProducts": {
    "products": [
      {
        "id": {
          "value": "globalPublicTest:adProduct:seekApi:GxWaGCHXEEif4qwbYFE33wPT7p5FEBuRrydf6q7oC31D"
        },
        "label": "Classic",
        "price": {
          "summary": "AUD 10.00"
        },
        "payment": {
          "summaryHtml": "We’ll deduct <strong>AUD 10.00</strong> from your contract."
        }
      },
      {
        "id": {
          "value": "globalPublicTest:adProduct:seekApi:GxWaGCHXEEif4qwbYFE33wKZtBrRhcUNXQSZmMMBRREB"
        },
        "label": "StandOut",
        "price": {
          "summary": "AUD 49.50"
        },
        "payment": {
          "summaryHtml": "We’ll deduct <strong>AUD 49.50</strong> from your contract."
        }
      }
    ]
  }
}
```
